/**
 * Pre-Submit BA Check Service
 *
 * Single BA call issued at the step 1 → step 2 transition carrying real Client,
 * real Participants, hotel services and selected auxiliaries (insurance + extras).
 * The resulting session/activityId flow into ConvertResponse + B/BQ/BC.
 */

import { performBookingAction } from './performAction';
import {
  formatDateForDaVinci,
  createClientFromFormData,
  createParticipantsFromTravelers,
  createMultiRoomServices,
  createAuxiliaryServices,
  type PpsExtraSelection,
} from './helpers';
import { DAVINCI_CONFIG } from './config';
import { toUtcMidnight } from '@/utils/formatting/dateFormatter';
import { detectAddressChanges, extractHints } from '@/utils/checkout/addressValidation';
import { isAvailableCode, isAddressValidationCode } from '@/constants/davinci/returnCodes';
import type { PreSubmitCheckResult } from '@/types/schemas/checkout/preSubmitCheck';
import type { CheckoutFormData } from '@/types/schemas/checkout';
import type { Traveler } from '@/types/Traveler';
import type { RoomCombination } from '@/services/mappers/offerMapper';
import type { SscInsurance } from '@/types/schemas/ssc';
import type { DeepReadonly } from 'vue';

export interface PreSubmitCheckParams {
  /** Validated contact form data (includes address + contact person) */
  formData: CheckoutFormData | DeepReadonly<CheckoutFormData>;
  /** Traveler summary (contact person when solo + additional travelers) */
  travelers: Traveler[];
  /** Room combination (same shape used by BA #1) */
  roomCombination: RoomCombination;
  /** GIATA product code */
  productCode: string;
  /** Selected SSC insurance (null when user skipped) */
  selectedInsurance: SscInsurance | null;
  /** Selected PPS extras with chosen ServiceType codes */
  selectedExtras: PpsExtraSelection[];
  /**
   * TravelType captured from the first BA response. Omit to fall back to
   * DAVINCI_CONFIG.TRAVEL_TYPE.
   */
  travelType?: string;
  /**
   * AgencyNumber derived from the captured TravelType. Omit to fall back to
   * DAVINCI_CONFIG.AGENCY_NUMBER (1).
   */
  agencyNumber?: number;
}

/**
 * Perform the merged pre-submit BA check.
 *
 * Response classification:
 *   - address correction (726/727 or detected data diff) → shouldStayOnForm=true,
 *     no session committed, user corrects address and retries
 *   - auxiliary unavailable (non-available code when aux was present) →
 *     shouldStayOnForm=true, auxUnavailable=true, user adjusts extras/insurance
 *   - available code (712/707/733/734/923) → baSession set, user proceeds
 *   - other → isValid=false, caller redirects to dead-end (or shows error)
 */
export async function performPreSubmitCheck(
  params: PreSubmitCheckParams
): Promise<PreSubmitCheckResult> {
  const {
    formData,
    travelers,
    roomCombination,
    productCode,
    selectedInsurance,
    selectedExtras,
    travelType,
    agencyNumber,
  } = params;

  const hotelServices = createMultiRoomServices(roomCombination, productCode);
  const hasAuxiliaries = selectedInsurance !== null || selectedExtras.length > 0;
  const auxiliaryServices = hasAuxiliaries
    ? createAuxiliaryServices({
        selectedInsurance,
        selectedExtras,
        checkIn: toUtcMidnight(roomCombination.startDate),
        checkOut: toUtcMidnight(roomCombination.endDate),
        participants: roomCombination.allPassengers,
        passengerCounts: roomCombination.passengerCounts,
      })
    : [];

  const client = createClientFromFormData(formData);
  const participants = createParticipantsFromTravelers(travelers);

  const bookingRequest = {
    ActionCode: 'BA' as const,
    AgencyNumber: agencyNumber ?? DAVINCI_CONFIG.AGENCY_NUMBER,
    Touroperator: DAVINCI_CONFIG.TOUROPERATOR,
    TravelType: travelType ?? DAVINCI_CONFIG.TRAVEL_TYPE,
    SalesOffice: '',
    BookingDate: formatDateForDaVinci(new Date()),
    Services: [...hotelServices, ...auxiliaryServices],
    Client: client,
    Participants: participants,
  };

  const response = await performBookingAction(bookingRequest, {});

  const booking = response.Booking as Record<string, unknown>;
  const code = (booking?.ReturnCode as number) ?? 999;

  const sentAddress = {
    street: formData.contactPerson.street,
    postalCode: formData.contactPerson.postalCode,
    city: formData.contactPerson.location,
    country: formData.contactPerson.country,
  };

  const responseClient = booking.Client as
    | { Address?: { Street?: string; ZipCode?: string; City?: string; Country?: string } }
    | undefined;
  const responseAddress = responseClient?.Address ?? {};
  const addressChanges = detectAddressChanges(sentAddress, responseAddress);
  const hasAddressChanges = addressChanges.length > 0;
  const apiHints = extractHints(booking as { Hints?: Array<{ Info?: string }> });

  const codeAvailable = isAvailableCode(code);
  const codeIsAddressValidation = isAddressValidationCode(code);
  // Address changes can co-occur with an aux rejection — surface both, otherwise
  // the user fixes their address, retries, and hits the same aux problem.
  const auxUnavailable = hasAuxiliaries && !codeAvailable && !codeIsAddressValidation;

  const shouldStayOnForm = codeIsAddressValidation || hasAddressChanges || auxUnavailable;

  // 726/727 are recoverable — treat as valid so the caller can surface corrections
  // instead of dead-ending.
  const isValid = codeAvailable || code === 726 || code === 727;

  const baSession =
    !shouldStayOnForm && isValid && response.SessionId && response.ActivityId
      ? {
          sessionId: response.SessionId,
          activityId: response.ActivityId,
          bookingResponse: booking,
        }
      : null;

  return {
    code,
    isValid,
    shouldStayOnForm,
    hasAddressChanges,
    addressChanges,
    correctedAddress: hasAddressChanges
      ? {
          street: responseAddress.Street,
          postalCode: responseAddress.ZipCode,
          city: responseAddress.City,
          country: responseAddress.Country,
        }
      : undefined,
    auxUnavailable,
    apiHints,
    baSession,
  };
}
